import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Cloud, DeviceMobile, WifiSlash, ArrowsClockwise, Lock, ShieldCheck } from "@phosphor-icons/react";
import { markModeSelected } from "@/hooks/useStorageMode";
import { initializeLocalDatabase } from "@/lib/localDatabase";

interface ModeSelectionProps {
  onComplete: () => void;
  onChooseCloud: () => void;
}

export function ModeSelection({ onComplete, onChooseCloud }: ModeSelectionProps) {
  const { t } = useTranslation();
  const [selectedMode, setSelectedMode] = useState<'local' | 'cloud' | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);

  const handleLocalMode = async () => {
    setSelectedMode('local');
    setIsInitializing(true);
    
    try {
      await initializeLocalDatabase();
      localStorage.setItem('axis_storage_mode', 'local');
      markModeSelected();
      onComplete();
    } catch (error) {
      console.error('Error initializing local database:', error);
      setIsInitializing(false);
    }
  };

  const handleCloudMode = () => {
    setSelectedMode('cloud');
    onChooseCloud();
  };

  return (
    <div className="fixed inset-0 bg-background overflow-hidden">
      {/* Animated background blobs - Liquid Glass style */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute w-[500px] h-[500px] rounded-full opacity-30"
          style={{
            background: 'radial-gradient(circle, hsl(var(--primary) / 0.4) 0%, transparent 70%)',
            top: '-10%',
            left: '50%',
            transform: 'translateX(-50%)',
            filter: 'blur(80px)',
          }}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.25, 0.35, 0.25],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute w-[400px] h-[400px] rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, hsl(var(--accent) / 0.5) 0%, transparent 70%)',
            bottom: '5%',
            right: '-15%',
            filter: 'blur(60px)',
          }}
          animate={{
            scale: [1, 1.15, 1],
            x: [0, -20, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute w-[300px] h-[300px] rounded-full opacity-15"
          style={{
            background: 'radial-gradient(circle, hsl(var(--primary) / 0.3) 0%, transparent 70%)',
            bottom: '30%',
            left: '-10%',
            filter: 'blur(50px)',
          }}
          animate={{
            scale: [1, 1.2, 1],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-8 safe-area-inset">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center mb-6"
        >
          <h1 className="text-[26px] font-bold text-foreground tracking-tight text-center leading-tight">
            {t('modeSelection.title', 'Где хранить ваши финансы?')}
          </h1>
          <p className="text-base text-muted-foreground mt-2 text-center max-w-[280px] leading-relaxed">
            {t('modeSelection.subtitle', 'Выберите удобный способ — это можно изменить позже')}
          </p>
        </motion.div>

        {/* Mode cards */}
        <div className="max-w-sm w-full space-y-3">
          {/* Local mode card - Glass style */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            onClick={handleLocalMode}
            disabled={isInitializing}
            whileTap={{ scale: 0.98 }}
            className="w-full text-left transition-all duration-300 active:scale-[0.98]"
          >
            <div 
              className={`p-5 rounded-3xl border transition-all duration-300 ${
                selectedMode === 'local'
                  ? 'border-primary/50 shadow-lg shadow-primary/10'
                  : 'border-border/30 hover:border-border/50'
              }`}
              style={{
                background: selectedMode === 'local' 
                  ? 'linear-gradient(145deg, hsl(var(--card) / 0.95) 0%, hsl(var(--card) / 0.85) 100%)'
                  : 'linear-gradient(145deg, hsl(var(--card) / 0.7) 0%, hsl(var(--card) / 0.5) 100%)',
                backdropFilter: 'blur(40px) saturate(180%)',
                WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                boxShadow: selectedMode === 'local'
                  ? '0 8px 32px -8px hsl(var(--primary) / 0.15), inset 0 1px 0 0 hsl(var(--background) / 0.5)'
                  : '0 4px 24px -8px hsl(var(--foreground) / 0.08), inset 0 1px 0 0 hsl(var(--background) / 0.4)',
              }}
            >
              <div className="flex items-start gap-4">
                {/* Icon container */}
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                  style={{
                    background: 'linear-gradient(145deg, hsl(35 100% 60% / 0.15) 0%, hsl(35 100% 50% / 0.08) 100%)',
                    boxShadow: 'inset 0 1px 0 0 hsl(35 100% 70% / 0.2)',
                  }}
                >
                  <DeviceMobile className="w-7 h-7 text-amber-500" weight="duotone" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground text-lg leading-tight">
                    {t('modeSelection.local.title', 'На устройстве')}
                  </h3>
                  <p className="text-muted-foreground text-[15px] mt-1.5 leading-relaxed">
                    {t('modeSelection.local.description', 'Данные хранятся только на вашем устройстве')}
                  </p>
                  
                  {/* Badges */}
                  <div className="flex items-center gap-5 mt-3">
                    <span className="flex items-center gap-2 text-sm text-muted-foreground">
                      <WifiSlash className="w-4 h-4" weight="duotone" />
                      {t('modeSelection.local.badge1', 'Офлайн')}
                    </span>
                    <span className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Lock className="w-4 h-4" weight="duotone" />
                      {t('modeSelection.local.badge2', 'Приватно')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.button>

          {/* Cloud mode card - Glass style */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.25 }}
            onClick={handleCloudMode}
            disabled={isInitializing}
            whileTap={{ scale: 0.98 }}
            className="w-full text-left transition-all duration-300 active:scale-[0.98]"
          >
            <div 
              className={`p-5 rounded-3xl border transition-all duration-300 ${
                selectedMode === 'cloud'
                  ? 'border-primary/50 shadow-lg shadow-primary/10'
                  : 'border-border/30 hover:border-border/50'
              }`}
              style={{
                background: selectedMode === 'cloud' 
                  ? 'linear-gradient(145deg, hsl(var(--card) / 0.95) 0%, hsl(var(--card) / 0.85) 100%)'
                  : 'linear-gradient(145deg, hsl(var(--card) / 0.7) 0%, hsl(var(--card) / 0.5) 100%)',
                backdropFilter: 'blur(40px) saturate(180%)',
                WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                boxShadow: selectedMode === 'cloud'
                  ? '0 8px 32px -8px hsl(var(--primary) / 0.15), inset 0 1px 0 0 hsl(var(--background) / 0.5)'
                  : '0 4px 24px -8px hsl(var(--foreground) / 0.08), inset 0 1px 0 0 hsl(var(--background) / 0.4)',
              }}
            >
              <div className="flex items-start gap-4">
                {/* Icon container */}
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                  style={{
                    background: 'linear-gradient(145deg, hsl(var(--primary) / 0.15) 0%, hsl(var(--primary) / 0.08) 100%)',
                    boxShadow: 'inset 0 1px 0 0 hsl(var(--primary) / 0.2)',
                  }}
                >
                  <Cloud className="w-7 h-7 text-primary" weight="duotone" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground text-lg leading-tight">
                    {t('modeSelection.cloud.title', 'В облаке')}
                  </h3>
                  <p className="text-muted-foreground text-[15px] mt-1.5 leading-relaxed">
                    {t('modeSelection.cloud.description', 'Доступ с любого устройства с синхронизацией и бэкапом')}
                  </p>
                  
                  {/* Badges */}
                  <div className="flex items-center gap-5 mt-3">
                    <span className="flex items-center gap-2 text-sm text-muted-foreground">
                      <ArrowsClockwise className="w-4 h-4" weight="duotone" />
                      {t('modeSelection.cloud.badge1', 'Синхронизация')}
                    </span>
                    <span className="flex items-center gap-2 text-sm text-muted-foreground">
                      <ShieldCheck className="w-4 h-4" weight="duotone" />
                      {t('modeSelection.cloud.badge2', 'Бэкап')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.button>
        </div>


        {/* Loading overlay - Glass style */}
        <AnimatePresence>
          {isInitializing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 flex items-center justify-center z-50"
              style={{
                background: 'hsl(var(--background) / 0.6)',
                backdropFilter: 'blur(12px) saturate(150%)',
                WebkitBackdropFilter: 'blur(12px) saturate(150%)',
              }}
            >
              <motion.div 
                className="flex flex-col items-center gap-4 p-8 rounded-3xl"
                style={{
                  background: 'linear-gradient(145deg, hsl(var(--card) / 0.8) 0%, hsl(var(--card) / 0.6) 100%)',
                  backdropFilter: 'blur(40px)',
                  boxShadow: '0 16px 48px -12px hsl(var(--foreground) / 0.15)',
                  border: '1px solid hsl(var(--border) / 0.3)',
                }}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
              >
                <div className="w-12 h-12 border-3 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-foreground font-medium">
                  {t('modeSelection.initializing', 'Подготовка...')}
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
